import { adminApi } from './ApiService';

class InventoryService {
  // ── Components ─────────────────────────────────────────
  async listComponents() {
    return adminApi.get('/admin/inventory/components');
  }

  async createComponent(data) {
    return adminApi.post('/admin/inventory/components', data);
  }

  async updateComponent(id, data) {
    return adminApi.patch(`/admin/inventory/components/${id}`, data);
  }

  async deleteComponent(id) {
    return adminApi.delete(`/admin/inventory/components/${id}`);
  }

  // ── Device Models ─────────────────────────────────────
  async listDeviceModels() {
    return adminApi.get('/admin/inventory/device-models');
  }

  async createDeviceModel(name) {
    return adminApi.post('/admin/inventory/device-models', { name });
  }

  async deleteDeviceModel(id) {
    return adminApi.delete(`/admin/inventory/device-models/${encodeURIComponent(id)}`);
  }

  async updateDeviceModel(id, data) {
    return adminApi.patch(`/admin/inventory/device-models/${encodeURIComponent(id)}`, data);
  }

  // ── BOM ────────────────────────────────────────────────
  async listBOM(model) {
    const params = model ? { model } : {};
    return adminApi.get('/admin/inventory/bom', params);
  }

  async createBOMEntry(data) {
    return adminApi.post('/admin/inventory/bom', data);
  }

  async deleteBOMEntry(id) {
    return adminApi.delete(`/admin/inventory/bom/${id}`);
  }

  // ── Sub-BOMs ───────────────────────────────────────────
  async listSubBOMs() {
    return adminApi.get('/admin/inventory/subbom');
  }

  async createSubBOM(data) {
    return adminApi.post('/admin/inventory/subbom', data);
  }

  async updateSubBOM(id, data) {
    return adminApi.patch(`/admin/inventory/subbom/${id}`, data);
  }

  async deleteSubBOM(id) {
    return adminApi.delete(`/admin/inventory/subbom/${id}`);
  }

  async assembleSubBOM(id, quantity) {
    return adminApi.post(`/admin/inventory/subbom/${id}/assemble`, { quantity });
  }

  // ── Stock Entries ──────────────────────────────────────
  async listEntries(componentId) {
    const params = componentId ? { componentId } : {};
    return adminApi.get('/admin/inventory/entries', params);
  }

  async createEntry(data) {
    return adminApi.post('/admin/inventory/entries', data);
  }

  async deleteEntry(id) {
    return adminApi.delete(`/admin/inventory/entries/${id}`);
  }

  // ── Movements ──────────────────────────────────────────
  async listMovements(filters = {}) {
    return adminApi.get('/admin/inventory/movements', filters);
  }

  async deleteMovement(id) {
    return adminApi.delete(`/admin/inventory/movements/${id}`);
  }

  // ── Actions ────────────────────────────────────────────
  async deductDevice(deviceModel, deviceMac, reason, excludedSubBoms) {
    return adminApi.post('/admin/inventory/deduct', { deviceModel, deviceMac, reason, excludedSubBoms });
  }

  async deductSubBom(subBomId, deviceMac, qty, reason) {
    return adminApi.post(`/admin/inventory/subbom/${subBomId}/deduct`, { qty, deviceMac, reason });
  }

  async adjustStock(componentId, quantity, reason) {
    return adminApi.post('/admin/inventory/adjust', { componentId, quantity, reason });
  }

  async createManualMovement({ componentId, quantity, reason, notes, deviceModel, deviceMac }) {
    return adminApi.post('/admin/inventory/manual-movement', { componentId, quantity, reason, notes, deviceModel, deviceMac });
  }

  // ── Dashboard ──────────────────────────────────────────
  async getDashboard() {
    return adminApi.get('/admin/inventory/dashboard');
  }

  // ── Products (retail items) ────────────────────────────
  async listProducts(params = {}) {
    return adminApi.get('/admin/products', params);
  }

  async createProduct(data) {
    return adminApi.post('/admin/products', data);
  }

  async updateProduct(id, data) {
    return adminApi.patch(`/admin/products/${id}`, data);
  }

  async deleteProduct(id) {
    return adminApi.delete(`/admin/products/${id}`);
  }

  async adjustProduct(productId, quantity, reason) {
    return adminApi.post('/admin/products/adjust', { productId, quantity, reason });
  }

  // ── Suppliers (Proveedores) ──────────────────────────────
  async listSuppliers() {
    return adminApi.get('/admin/suppliers');
  }

  async createSupplier(data) {
    return adminApi.post('/admin/suppliers', data);
  }

  async updateSupplier(id, data) {
    return adminApi.patch(`/admin/suppliers/${id}`, data);
  }

  async deleteSupplier(id) {
    return adminApi.delete(`/admin/suppliers/${id}`);
  }

  // ── Purchase Orders (Órdenes de Compra) ──────────────────
  async listPurchaseOrders() {
    return adminApi.get('/admin/purchase-orders');
  }

  async getNextPONumber() {
    return adminApi.get('/admin/purchase-orders/next-number');
  }

  async createPurchaseOrder(data) {
    return adminApi.post('/admin/purchase-orders', data);
  }

  async updatePurchaseOrder(id, data) {
    return adminApi.patch(`/admin/purchase-orders/${id}`, data);
  }

  async sendPurchaseOrder(id) {
    return adminApi.post(`/admin/purchase-orders/${id}/send`);
  }

  async receivePurchaseOrder(id) {
    return adminApi.post(`/admin/purchase-orders/${id}/receive`);
  }

  async cancelPurchaseOrder(id) {
    return adminApi.post(`/admin/purchase-orders/${id}/cancel`);
  }

  async deletePurchaseOrder(id) {
    return adminApi.delete(`/admin/purchase-orders/${id}`);
  }

  // ── Stock Items (Fabricated units available for sale) ──
  async listStockItems(params = {}) {
    return adminApi.get('/admin/stock-items', params);
  }

  async createStockItem(data) {
    return adminApi.post('/admin/stock-items', data);
  }

  async updateStockItem(id, data) {
    return adminApi.patch(`/admin/stock-items/${id}`, data);
  }

  async deleteStockItem(id) {
    return adminApi.delete(`/admin/stock-items/${id}`);
  }

  async bulkReserveStockItems(ids, invoiceId) {
    return adminApi.post('/admin/stock-items/bulk-reserve', { ids, invoiceId });
  }

  async backfillStockItems() {
    return adminApi.post('/admin/stock-items/backfill');
  }

  // ── Supplier Price Search (Scraping) ───────────────────
  async searchSupplierPrices(query) {
    return adminApi.get('/admin/supplier-search', { q: query });
  }
}

export default new InventoryService();
